/**
 * Dashboard API Service
 * Handles all dashboard-related API calls
 */
import api from './api';

// Mock data generator
const generateMockData = () => {
  const currentTime = new Date();

  return {
    todays_metrics: {
      total_arrivals: 24,
      in_process: 8,
      completed: 14,
      delayed: 2,
      average_processing_time: 45,
      dock_occupancy: 67,
      qc_pass_rate: 92,
      sla_compliance: 88
    },
    live_trucks: [
      {
        id: 1,
        truck_number: 'MH-12-AB-1234',
        supplier: 'ABC Suppliers Ltd',
        material_type: 'JIT',
        po_number: 'PO-2024-001',
        current_step: 'QC Inspection',
        status: 'IN_PROGRESS',
        arrival_time: '09:30 AM',
        elapsed_time: '2h 15m',
        sla_status: 'ON_TIME',
        progress: 60
      },
      {
        id: 2,
        truck_number: 'KA-05-XY-5678',
        supplier: 'XYZ Trading Co',
        material_type: 'Standard',
        po_number: 'PO-2024-002',
        current_step: 'Dock Assignment',
        status: 'IN_PROGRESS',
        arrival_time: '10:15 AM',
        elapsed_time: '1h 30m',
        sla_status: 'ON_TIME',
        progress: 40
      },
      {
        id: 3,
        truck_number: 'DL-08-CD-9012',
        supplier: 'Global Parts Inc',
        material_type: 'FOC',
        po_number: 'PO-2024-003',
        current_step: 'Unloading',
        status: 'IN_PROGRESS',
        arrival_time: '11:00 AM',
        elapsed_time: '45m',
        sla_status: 'ON_TIME',
        progress: 75
      },
      {
        id: 4,
        truck_number: 'TN-22-EF-3456',
        supplier: 'Prime Logistics',
        material_type: 'Critical',
        po_number: 'PO-2024-004',
        current_step: 'Gate Entry',
        status: 'DELAYED',
        arrival_time: '08:00 AM',
        elapsed_time: '3h 45m',
        sla_status: 'DELAYED',
        progress: 20
      },
      {
        id: 5,
        truck_number: 'MH-01-GH-7890',
        supplier: 'Metro Suppliers',
        material_type: 'Return',
        po_number: 'PO-2024-005',
        current_step: 'Documentation',
        status: 'IN_PROGRESS',
        arrival_time: '10:45 AM',
        elapsed_time: '1h 00m',
        sla_status: 'ON_TIME',
        progress: 30
      }
    ],
    dock_utilization: {
      total_docks: 6,
      occupied: 4,
      available: 2,
      blocked: 0,
      docks: [
        { number: 'DOCK-01', status: 'OCCUPIED', truck: 'MH-12-AB-1234', duration: '2h 15m' },
        { number: 'DOCK-02', status: 'AVAILABLE', truck: null, duration: null },
        { number: 'DOCK-03', status: 'OCCUPIED', truck: 'KA-05-XY-5678', duration: '1h 30m' },
        { number: 'DOCK-04', status: 'OCCUPIED', truck: 'DL-08-CD-9012', duration: '45m' },
        { number: 'DOCK-05', status: 'AVAILABLE', truck: null, duration: null },
        { number: 'DOCK-06', status: 'OCCUPIED', truck: 'MH-01-GH-7890', duration: '1h 00m' }
      ]
    },
    qc_status: {
      pending: 3,
      in_progress: 2,
      passed: 15,
      failed: 1,
      inspections: [
        {
          id: 1,
          truck: 'MH-12-AB-1234',
          supplier: 'ABC Suppliers Ltd',
          status: 'IN_PROGRESS',
          inspector: 'John Doe',
          started_at: '11:00 AM'
        },
        {
          id: 2,
          truck: 'DL-08-CD-9012',
          supplier: 'Global Parts Inc',
          status: 'PENDING',
          inspector: null,
          started_at: null
        },
        {
          id: 3,
          truck: 'KA-01-IJ-2468',
          supplier: 'Quality Parts Ltd',
          status: 'PASSED',
          inspector: 'Jane Smith',
          started_at: '09:30 AM',
          completed_at: '10:45 AM'
        }
      ]
    },
    alerts: [
      {
        id: 1,
        type: 'CRITICAL',
        message: 'Truck TN-22-EF-3456 delayed beyond SLA',
        timestamp: '11:30 AM',
        priority: 'HIGH'
      },
      {
        id: 2,
        type: 'WARNING',
        message: 'DOCK-03 approaching capacity limit',
        timestamp: '11:15 AM',
        priority: 'MEDIUM'
      },
      {
        id: 3,
        type: 'INFO',
        message: 'QC inspection completed for PO-2024-001',
        timestamp: '10:45 AM',
        priority: 'LOW'
      },
      {
        id: 4,
        type: 'WARNING',
        message: 'Supplier ABC Suppliers Ltd has 2 pending inspections',
        timestamp: '10:30 AM',
        priority: 'MEDIUM'
      },
      {
        id: 5,
        type: 'INFO',
        message: 'GRN created for truck MH-01-AB-5555',
        timestamp: '10:00 AM',
        priority: 'LOW'
      }
    ],
    supplier_performance: {
      top_performers: [
        { name: 'ABC Suppliers Ltd', on_time_delivery: 95, quality_score: 98, total_deliveries: 42 },
        { name: 'XYZ Trading Co', on_time_delivery: 92, quality_score: 96, total_deliveries: 38 },
        { name: 'Global Parts Inc', on_time_delivery: 88, quality_score: 94, total_deliveries: 35 }
      ],
      needs_attention: [
        { name: 'Prime Logistics', on_time_delivery: 65, quality_score: 72, total_deliveries: 28 },
        { name: 'Budget Parts Co', on_time_delivery: 58, quality_score: 68, total_deliveries: 22 }
      ]
    }
  };
};

class DashboardService {
  constructor() {
    // Flag to use mock data (set to false to use live API data)
    // Using live API data now that backend is integrated with inbound_operations
    this.useMockData = false;
  }

  /**
   * Get complete dashboard summary
   */
  async getDashboardSummary() {
    if (this.useMockData) {
      // Simulate API delay
      await new Promise(resolve => setTimeout(resolve, 500));
      return generateMockData();
    }
    const response = await api.get('/workflow/dashboard/summary/');
    return response.data;
  }

  /**
   * Get live truck status
   */
  async getLiveTruckStatus() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().live_trucks;
    }
    const response = await api.get('/workflow/dashboard/live-trucks/');
    return response.data;
  }

  /**
   * Get dock utilization
   */
  async getDockUtilization() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().dock_utilization;
    }
    const response = await api.get('/workflow/dashboard/dock-utilization/');
    return response.data;
  }

  /**
   * Get QC status board
   */
  async getQCStatus() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().qc_status;
    }
    const response = await api.get('/workflow/dashboard/qc-status/');
    return response.data;
  }

  /**
   * Get today's metrics
   */
  async getTodaysMetrics() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().todays_metrics;
    }
    const response = await api.get('/workflow/dashboard/todays-metrics/');
    return response.data;
  }

  /**
   * Get alerts panel
   */
  async getAlerts() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().alerts;
    }
    const response = await api.get('/workflow/dashboard/alerts/');
    return response.data;
  }

  /**
   * Get supplier performance
   */
  async getSupplierPerformance() {
    if (this.useMockData) {
      await new Promise(resolve => setTimeout(resolve, 300));
      return generateMockData().supplier_performance;
    }
    const response = await api.get('/workflow/dashboard/supplier-performance/');
    return response.data;
  }

  /**
   * Get performance trends
   * @param {string} period - 'daily' or 'weekly'
   * @param {number} days - number of days for daily trend (default: 7)
   * @param {number} weeks - number of weeks for weekly trend (default: 4)
   */
  async getPerformanceTrends(period = 'daily', params = {}) {
    const queryParams = new URLSearchParams({
      period,
      ...params
    });
    const response = await api.get(`/workflow/dashboard/trends/?${queryParams}`);
    return response.data;
  }
}

export default new DashboardService();
